import { usePage } from '@inertiajs/vue3';

/**
 * v-can Directive
 *
 * Custom Vue directive for conditional rendering based on user permissions.
 * Shows or hides elements based on whether the user has the required permission(s).
 *
 * Usage Examples:
 *
 * Single permission:
 * <button v-can="'system.roles.manage'">Manage Roles</button>
 *
 * Any of multiple permissions (OR logic):
 * <div v-can:any="['tickets.edit', 'tickets.delete']">Actions</div>
 *
 * All of multiple permissions (AND logic):
 * <div v-can:all="['tickets.view', 'tickets.export']">Advanced Options</div>
 *
 * Opposite - show if user DOESN'T have permission:
 * <div v-cannot="'system.roles.manage'">Limited Access Message</div>
 *
 * Cannot with multiple permissions:
 * <div v-cannot:any="['admin.access', 'super.admin']">Non-Admin Content</div>
 *
 * Phase 9: Roles & Permissions Management UI
 */

/**
 * Check if user has the required permission(s).
 *
 * @param {string|string[]} value - Permission(s) to check
 * @param {string|undefined} arg - Modifier ('any', 'all', or undefined)
 * @param {string[]} permissions - User's permissions array
 * @returns {boolean} - True if user has required permission(s)
 */
function checkPermission(value, arg, permissions) {
  // Handle empty or invalid value
  if (!value) {
    return false;
  }

  // Single permission check
  if (typeof value === 'string') {
    return permissions.includes(value);
  }

  // Multiple permissions check
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return false;
    }

    if (arg === 'any') {
      // Check if user has ANY of the permissions (OR logic)
      return value.some(permission => permissions.includes(permission));
    } else if (arg === 'all') {
      // Check if user has ALL of the permissions (AND logic)
      return value.every(permission => permissions.includes(permission));
    } else {
      // Default to 'any' if array provided without modifier
      return value.some(permission => permissions.includes(permission));
    }
  }

  return false;
}

/**
 * Update element visibility based on permission check.
 *
 * @param {HTMLElement} el - The DOM element
 * @param {boolean} hasPermission - Whether user has permission
 */
function updateElementVisibility(el, hasPermission) {
  if (hasPermission) {
    // Show element
    el.style.removeProperty('display');
    // Remove the data attribute that marks it as hidden
    el.removeAttribute('data-v-can-hidden');
  } else {
    // Hide element using display: none
    el.style.display = 'none';
    // Mark element as hidden by directive
    el.setAttribute('data-v-can-hidden', 'true');
  }
}

/**
 * Process the directive binding and update element visibility.
 *
 * @param {HTMLElement} el - The DOM element
 * @param {Object} binding - Vue directive binding object
 */
function processDirective(el, binding) {
  try {
    const page = usePage();
    const permissions = page.props.auth?.permissions || [];

    // Check if user has required permission(s)
    let hasPermission = checkPermission(binding.value, binding.arg, permissions);

    // Handle v-cannot (opposite logic)
    if (binding.name === 'cannot') {
      hasPermission = !hasPermission;
    }

    // Update element visibility
    updateElementVisibility(el, hasPermission);
  } catch (error) {
    console.error('[v-can directive] Error:', error);
    // In case of error, hide the element for security
    el.style.display = 'none';
  }
}

/**
 * Vue directive definition for v-can and v-cannot.
 */
export default {
  /**
   * Called when the directive is first mounted to the element.
   *
   * @param {HTMLElement} el - The element the directive is bound to
   * @param {Object} binding - Directive binding object
   */
  mounted(el, binding) {
    processDirective(el, binding);
  },

  /**
   * Called when the component containing the directive is updated.
   * This ensures the directive responds to prop changes.
   *
   * @param {HTMLElement} el - The element the directive is bound to
   * @param {Object} binding - Directive binding object
   */
  updated(el, binding) {
    // Only reprocess if the value or arg has changed
    if (
      binding.value !== binding.oldValue ||
      binding.arg !== binding.oldArg
    ) {
      processDirective(el, binding);
    }
  },

  /**
   * Called before the element is unmounted.
   * Clean up any modifications made by the directive.
   *
   * @param {HTMLElement} el - The element the directive is bound to
   */
  beforeUnmount(el) {
    // Remove any inline styles added by the directive
    if (el.hasAttribute('data-v-can-hidden')) {
      el.style.removeProperty('display');
      el.removeAttribute('data-v-can-hidden');
    }
  },
};
